import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface ParentCardProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  actions?: React.ReactNode;
  isLoading?: boolean;
  variant?: 'default' | 'elevated' | 'glass';
  className?: string;
}

export const ParentCard: React.FC<ParentCardProps> = ({
  children,
  title,
  description,
  icon: Icon,
  actions,
  isLoading = false,
  variant = 'default',
  className
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'elevated':
        return 'shadow-lg border-0 bg-white';
      case 'glass':
        return 'bg-white/70 backdrop-blur-md border-white/30 shadow-lg';
      default:
        return 'bg-white border-gray-200 shadow-sm';
    }
  };

  if (isLoading) {
    return (
      <Card className={cn(getVariantClasses(), className)}>
        {(title || description || Icon) && (
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {Icon && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                    <Skeleton className="h-4 w-4" />
                  </div>
                )}
                <div>
                  {title && <Skeleton className="h-6 w-32 mb-2" />}
                  {description && <Skeleton className="h-4 w-48" />}
                </div>
              </div>
              {actions && <Skeleton className="h-9 w-24" />}
            </div>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(getVariantClasses(), className)}>
      {(title || description || Icon || actions) && (
        <CardHeader className="pb-3 sm:pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {Icon && (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 shrink-0">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                {title && (
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">
                    {title}
                  </h3>
                )}
                {description && (
                  <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                    {description}
                  </p>
                )}
              </div>
            </div>
            {actions && (
              <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 shrink-0">
                {actions}
              </div>
            )}
          </div>
        </CardHeader>
      )}
      <CardContent className="pt-0">
        {children}
      </CardContent>
    </Card>
  );
};

export default ParentCard;